import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus, Eye, EyeOff, ArrowDownUp,
  MoreHorizontal, Inbox, Building2, ChevronDown, ChevronRight,
} from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useSettings, formatMoney } from "../lib/settings";
import FilterPopover from "../components/FilterPopover";
import DuplicateWarningModal from "../components/DuplicateWarningModal";
import { checkDuplicates } from "../lib/dedupe";
import CompanyAutocomplete from "../components/CompanyAutocomplete";
import PragmaSelect from "../components/PragmaSelect";

// ─── Disposition mapping ───
// Internal API values → display label + Stratus pill class.
const DISP = {
  not_contacted:             { label: "Not Contacted", cls: "disp-not-contacted", group: "not_contacted" },
  contact_attempted:         { label: "Contacted",     cls: "disp-contacted",     group: "contacted" },
  call_back_requested:       { label: "Contacted",     cls: "disp-contacted",     group: "contacted" },
  qualification_in_progress: { label: "In Progress",   cls: "disp-in-progress",   group: "in_progress" },
  qualified:                 { label: "Qualified",     cls: "disp-qualified",     group: "qualified" },
  not_contactable:           { label: "Not Qualified", cls: "disp-not-qualified", group: "not_qualified" },
  not_interested:            { label: "Not Qualified", cls: "disp-not-qualified", group: "not_qualified" },
  not_qualified:             { label: "Not Qualified", cls: "disp-not-qualified", group: "not_qualified" },
};

const initialsFrom = (name) =>
  (name || "?").split(/\s+/).filter(Boolean).slice(0, 2).map(p => p[0]).join("").toUpperCase();

const dispEntry = (key) => DISP[key] || DISP.not_contacted;

export default function LeadInbox() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [moves, setMoves] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc"); // 'asc' | 'desc'
  const [filterState, setFilterState] = useState({ disposition: [], owner: [] });

  const load = async () => {
    setLoading(true);
    try {
      const [l, u, m] = await Promise.all([
        api.get(`/leads?show_all=${showAll}`),
        api.get("/users"),
        api.get("/moves?filter=upcoming"),
      ]);
      setLeads(l.data); setUsers(u.data); setMoves(m.data);
    } catch { toast.error("Failed to load leads"); }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showAll]);

  const userById = (id) => users.find((u) => u.id === id);
  const nextMove = (leadId) => {
    const m = moves
      .filter((m) => m.linked_type === "lead" && m.linked_id === leadId)
      .sort((a, b) => (a.due_date > b.due_date ? 1 : -1))[0];
    return m?.title || null;
  };

  // ─── Search + filter + sort ───
  const filtered = useMemo(() => {
    let rows = leads;
    if (search) {
      const s = search.toLowerCase();
      rows = rows.filter((l) =>
        (l.name || "").toLowerCase().includes(s)
        || (l.company || "").toLowerCase().includes(s)
        || (l.email || "").toLowerCase().includes(s)
      );
    }
    if (filterState.disposition.length > 0) {
      const setKeys = new Set(filterState.disposition);
      rows = rows.filter((l) => setKeys.has(dispEntry(l.disposition).group));
    }
    if (filterState.owner.length > 0) {
      const oids = new Set(filterState.owner);
      rows = rows.filter((l) => oids.has(l.owner_id));
    }
    const dir = sortDir === "asc" ? 1 : -1;
    const get = (l) => {
      if (sortKey === "name") return (l.name || "").toLowerCase();
      if (sortKey === "next_move") return (nextMove(l.id) || "").toLowerCase();
      if (sortKey === "created_at") return l.created_at || "";
      if (sortKey === "owner") return (userById(l.owner_id)?.name || "").toLowerCase();
      if (sortKey === "disposition") return dispEntry(l.disposition).label;
      return "";
    };
    return [...rows].sort((a, b) => {
      const av = get(a), bv = get(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leads, search, sortKey, sortDir, moves, users, filterState]);

  // ─── Stat counts ───
  const stats = useMemo(() => {
    const counts = { total: leads.length, not_contacted: 0, in_progress: 0, qualified: 0 };
    leads.forEach((l) => {
      const g = dispEntry(l.disposition).group;
      if (g === "not_contacted") counts.not_contacted++;
      if (g === "in_progress")   counts.in_progress++;
      if (g === "qualified")     counts.qualified++;
    });
    return counts;
  }, [leads]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };

  const sortIcon = (key) => {
    if (sortKey !== key) return <span className="sort-icon">↕</span>;
    return <span className="sort-icon">{sortDir === "asc" ? "↑" : "↓"}</span>;
  };

  const firstName = user?.first_name || (user?.name || "").split(" ")[0] || "there";

  const onRowClick = (lead) => navigate(`/leads/${lead.id}`);

  return (
    <div data-testid="lead-inbox-page">
      {/* ─── Page header ─── */}
      <div className="stratus-page-header">
        <div>
          <div className="stratus-page-title" data-testid="page-title">Lead Inbox</div>
          <div className="stratus-page-sub">
            {filtered.length} lead{filtered.length === 1 ? "" : "s"}
            &nbsp;·&nbsp; Pre-qualification
            &nbsp;·&nbsp; Hi {firstName} <span role="img" aria-label="wave">👋</span>
          </div>
        </div>
        <div className="stratus-actions">
          <button
            type="button"
            className={`ghost-pill ${!showAll ? "active" : ""}`}
            onClick={() => setShowAll((s) => !s)}
            data-testid="toggle-show-all-leads"
            title={showAll ? "Showing all" : "Showing mine only"}
          >
            {showAll ? <Eye /> : <EyeOff />}
            {showAll ? "All leads" : "My leads"}
          </button>
          <button
            type="button"
            className="btn-pill-primary"
            onClick={() => setShowForm(true)}
            data-testid="new-lead-btn"
          >
            <Plus strokeWidth={2.5} /> New Lead
          </button>
        </div>
      </div>

      {/* ─── Stat cards ─── */}
      <div className="stats-row" data-testid="leads-stats-row">
        <div className="stat-card" data-testid="stat-total-leads">
          <div className="stat-label">Total Leads</div>
          <div className="stat-value">{stats.total}</div>
          <div className="stat-badge badge-mute">Pre-qualification</div>
        </div>
        <div className="stat-card" data-testid="stat-not-contacted">
          <div className="stat-label">Not Contacted</div>
          <div className="stat-value">{stats.not_contacted}</div>
          <div className="stat-badge badge-orange">Needs action</div>
        </div>
        <div className="stat-card" data-testid="stat-in-progress">
          <div className="stat-label">In Progress</div>
          <div className="stat-value">{stats.in_progress}</div>
          <div className="stat-badge badge-purple">Active</div>
        </div>
        <div className="stat-card" data-testid="stat-qualified">
          <div className="stat-label">Qualified</div>
          <div className="stat-value">{stats.qualified}</div>
          <div className="stat-badge badge-lime">This month</div>
        </div>
      </div>

      {/* ─── Toolbar ─── */}
      <div className="stratus-toolbar">
        <FilterPopover
          testid="leads-filter"
          matchCount={filtered.length}
          value={filterState}
          onChange={setFilterState}
          groups={[
            {
              key: "disposition",
              title: "Disposition",
              options: [
                { value: "not_contacted", label: "Not Contacted" },
                { value: "contacted",     label: "Contacted" },
                { value: "in_progress",   label: "In Progress" },
                { value: "qualified",     label: "Qualified" },
                { value: "not_qualified", label: "Not Qualified" },
              ],
            },
            {
              key: "owner",
              title: "Owner",
              options: users
                .filter((u) => u.active !== false)
                .map((u) => ({ value: u.id, label: u.name })),
            },
          ]}
        />
        <button
          type="button"
          className="ghost-pill"
          data-testid="leads-sort-btn"
          onClick={() => toggleSort(sortKey)}
        >
          <ArrowDownUp /> Sort
        </button>
      </div>

      {/* ─── Table card ─── */}
      <div className="stratus-table-card">
        {loading ? (
          <div className="table-empty">
            <div className="table-empty-sub mono">loading…</div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="table-empty" data-testid="leads-empty">
            <div className="table-empty-icon"><Inbox strokeWidth={1.6} /></div>
            <div className="table-empty-title">{search ? "No leads found" : "No leads yet"}</div>
            <div className="table-empty-sub">
              {search ? "Try adjusting your search or filters" : "Click \"New Lead\" to add your first one."}
            </div>
          </div>
        ) : (
          <table className="stratus-table" data-testid="leads-table">
            <thead>
              <tr>
                <th className={`sortable ${sortKey === "name" ? "sorted" : ""}`} onClick={() => toggleSort("name")}>
                  Name {sortIcon("name")}
                </th>
                <th className={`sortable ${sortKey === "next_move" ? "sorted" : ""}`} onClick={() => toggleSort("next_move")}>
                  Next Move {sortIcon("next_move")}
                </th>
                <th className={`sortable ${sortKey === "created_at" ? "sorted" : ""}`} onClick={() => toggleSort("created_at")}>
                  Date Created {sortIcon("created_at")}
                </th>
                <th className={`sortable ${sortKey === "owner" ? "sorted" : ""}`} onClick={() => toggleSort("owner")}>
                  Owner {sortIcon("owner")}
                </th>
                <th className={`sortable ${sortKey === "disposition" ? "sorted" : ""}`} onClick={() => toggleSort("disposition")}>
                  Disposition {sortIcon("disposition")}
                </th>
                <th style={{ width: 130 }} />
              </tr>
            </thead>
            <tbody>
              {filtered.map((lead) => {
                const d = dispEntry(lead.disposition);
                const owner = userById(lead.owner_id);
                const nm = nextMove(lead.id);
                return (
                  <tr
                    key={lead.id}
                    onClick={() => onRowClick(lead)}
                    data-testid={`lead-row-${lead.id}`}
                  >
                    <td>
                      <div className="lead-name">{lead.name}</div>
                      <div className="lead-company">{lead.company || lead.email || "—"}</div>
                    </td>
                    <td>
                      {nm ? <span>{nm}</span> : <span className="next-move-empty">—</span>}
                    </td>
                    <td><span className="date-cell">{(lead.created_at || "").slice(0, 10)}</span></td>
                    <td>
                      <div className="owner-cell">
                        <div className="owner-avatar">{initialsFrom(owner?.name)}</div>
                        <span className="owner-name">{owner?.name || "—"}</span>
                      </div>
                    </td>
                    <td><span className={`disp-pill ${d.cls}`}>{d.label}</span></td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="row-actions">
                        <button
                          type="button"
                          className="row-action-btn"
                          title="Add move"
                          data-testid={`lead-add-move-${lead.id}`}
                          onClick={(e) => { e.stopPropagation(); navigate(`/leads/${lead.id}?action=add-move`); }}
                        >
                          <Plus />
                        </button>
                        <button
                          type="button"
                          className="row-action-btn"
                          title="View"
                          data-testid={`lead-view-${lead.id}`}
                          onClick={(e) => { e.stopPropagation(); navigate(`/leads/${lead.id}`); }}
                        >
                          <Eye />
                        </button>
                        <button
                          type="button"
                          className="row-action-btn"
                          title="More"
                          data-testid={`lead-more-${lead.id}`}
                          onClick={(e) => { e.stopPropagation(); navigate(`/leads/${lead.id}`); }}
                        >
                          <MoreHorizontal />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {showForm && (
        <NewLeadForm
          users={users}
          currentUserId={user.id}
          onClose={() => setShowForm(false)}
          onCreated={() => { setShowForm(false); load(); }}
        />
      )}
    </div>
  );
}

function NewLeadForm({ users, currentUserId, onClose, onCreated }) {
  const navigate = useNavigate();
  const { currency } = useSettings();
  const [sources, setSources] = useState([]);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "", source: "",
    value: "", notes: "", owner_id: currentUserId, disposition: "not_contacted",
    company_id: null, role: "",
  });
  const [saving, setSaving] = useState(false);
  const [dedupeMatches, setDedupeMatches] = useState(null);
  const [createCompany, setCreateCompany] = useState(false);
  const [companyExists, setCompanyExists] = useState(false);
  const [companyForm, setCompanyForm] = useState({
    website: "", domain: "", industry: "", employees: "",
  });

  useEffect(() => {
    api.get("/sources").then((r) => setSources(r.data)).catch(() => {});
  }, []);

  // Check whether the typed company name matches an existing catalog entry.
  // Turns off createCompany suggestion when a match is found.
  useEffect(() => {
    const name = (form.company || "").trim();
    if (!name || form.company_id) { setCompanyExists(false); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/companies/search", { params: { q: name, limit: 5 } });
        if (cancelled) return;
        setCompanyExists((data || []).some(c => c.name.toLowerCase() === name.toLowerCase()));
      } catch { /* ignore */ }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [form.company, form.company_id]);

  // Any time an existing company gets picked, cancel create-company mode.
  useEffect(() => {
    if (form.company_id) setCreateCompany(false);
  }, [form.company_id]);

  const doCreate = async () => {
    setSaving(true);
    try {
      let companyIdToLink = form.company_id;
      // Create the new Company first (if user chose that flow) so the lead
      // is linked to a real catalog entry, not a free-text stub.
      if (createCompany && !form.company_id && (form.company || "").trim()) {
        if (!companyForm.industry) { toast.error("Industry is required"); setSaving(false); return; }
        if (!companyForm.employees) { toast.error("Company size is required"); setSaving(false); return; }
        const payload = {
          name: form.company.trim(),
          domain: (companyForm.domain || "").trim() || null,
          website: (companyForm.website || "").trim() || null,
          industry: companyForm.industry || null,
          employees: companyForm.employees || null,
          source: form.source || null,
        };
        try {
          const { data: created } = await api.post("/companies", payload);
          companyIdToLink = created.id;
        } catch (e) {
          toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to create company");
          setSaving(false);
          return;
        }
      }
      await api.post("/leads", { ...form, company_id: companyIdToLink, value: parseFloat(form.value) });
      toast.success("Lead created");
      onCreated();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to create lead");
    }
    setSaving(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.source) { toast.error("Source is required"); return; }
    if (form.value === "" || isNaN(parseFloat(form.value))) {
      toast.error("Lead value is required");
      return;
    }
    setSaving(true);
    const { hasMatches, matches } = await checkDuplicates({
      entity_type: "lead", email: form.email, phone: form.phone, company: form.company,
    });
    setSaving(false);
    if (hasMatches) { setDedupeMatches(matches); return; }
    doCreate();
  };

  return (
    <>
      <div className="side-panel-backdrop" onClick={onClose} />
      <div className="side-panel" data-variant="center" data-testid="new-lead-panel">
        <div className="px-6 py-5" style={{ borderBottom: "1px solid var(--pg-border)" }}>
          <h2 className="text-xl">New Lead</h2>
          <div className="mono text-[11px] text-mute mt-1">add a lead to your inbox</div>
        </div>
        <form onSubmit={submit} className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <Field label="Name" required>
            <input className="pg-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="lead-input-name" />
          </Field>
          <Field label="Email" required>
            <input className="pg-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="lead-input-email" />
          </Field>
          <Field label="Phone" required>
            <input className="pg-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required data-testid="lead-input-phone" />
          </Field>
          <Field label="Company">
            <CompanyAutocomplete
              value={form.company}
              companyId={form.company_id}
              onChange={({ name, company_id }) => setForm({ ...form, company: name, company_id })}
              inputTestId="lead-input-company"
            />
            {form.company?.trim() && !form.company_id && !companyExists && (
              <button
                type="button"
                onClick={() => setCreateCompany((v) => !v)}
                className="pg-btn pg-btn-ghost"
                data-testid="create-company-toggle"
                style={{ marginTop: 8, fontSize: 12, padding: "6px 10px" }}
              >
                {createCompany ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                <Building2 size={12} /> Create COMPANY "{form.company.trim()}"
              </button>
            )}
            {createCompany && form.company?.trim() && !form.company_id && (
              <div
                data-testid="new-company-fields"
                style={{
                  marginTop: 10, padding: 14,
                  border: "1px solid var(--pg-border)", borderRadius: 8,
                  background: "var(--pg-surface-alt, rgba(0,0,0,0.02))",
                }}
              >
                <div className="mono text-[10px] uppercase tracking-wider" style={{ color: "var(--pg-text-mute)", marginBottom: 10 }}>
                  Required company details
                </div>
                <Field label="Website">
                  <input
                    className="pg-input"
                    value={companyForm.website}
                    onChange={(e) => setCompanyForm({ ...companyForm, website: e.target.value })}
                    placeholder="https://example.com"
                    data-testid="new-company-website"
                  />
                </Field>
                <Field label="Domain">
                  <input
                    className="pg-input"
                    value={companyForm.domain}
                    onChange={(e) => setCompanyForm({ ...companyForm, domain: e.target.value })}
                    placeholder="example.com"
                    data-testid="new-company-domain"
                  />
                </Field>
                <Field label="Industry" required>
                  <PragmaSelect
                    value={companyForm.industry}
                    onChange={(v) => setCompanyForm({ ...companyForm, industry: v })}
                    options={[
                      "Technology","Finance","Healthcare","Real Estate","Retail",
                      "Education","Legal","Construction","Marketing","Other",
                    ].map((i) => ({ value: i, label: i }))}
                    placeholder="Pick an industry…"
                    testId="new-company-industry"
                  />
                </Field>
                <Field label="Company Size" required>
                  <PragmaSelect
                    value={companyForm.employees}
                    onChange={(v) => setCompanyForm({ ...companyForm, employees: v })}
                    options={["1-10","11-50","51-200","201-500","501-1000","1000+"].map((e) => ({ value: e, label: `${e} employees` }))}
                    placeholder="Pick a size…"
                    testId="new-company-employees"
                  />
                </Field>
              </div>
            )}
          </Field>
          <Field label="Role / Job Title">
            <input
              className="pg-input"
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              placeholder="e.g. Head of Marketing, CTO"
              data-testid="lead-input-role"
            />
          </Field>
          <Field label="Source" required>
            <PragmaSelect
              value={form.source}
              onChange={(v) => setForm({ ...form, source: v })}
              options={sources.map((s) => ({ value: s.name, label: s.name }))}
              placeholder="Choose a source…"
              testId="lead-input-source"
            />
          </Field>
          <Field label={`Lead Value (${currency.code})`} required>
            <div className="flex items-center gap-2">
              <span className="mono text-sm text-mute" style={{ minWidth: 22 }}>{currency.symbol}</span>
              <input
                className="pg-input"
                type="number" min="0" step="0.01"
                value={form.value}
                onChange={(e) => setForm({ ...form, value: e.target.value })}
                required
                placeholder="0.00"
                data-testid="lead-input-value"
              />
            </div>
            <div className="mono text-[10px] text-mute mt-1">Will display as {formatMoney(form.value || 0, currency.symbol)}</div>
          </Field>
          <Field label="Owner">
            <PragmaSelect
              value={form.owner_id}
              onChange={(v) => setForm({ ...form, owner_id: v })}
              options={users.filter((u) => u.active !== false).map((u) => ({ value: u.id, label: u.name, hint: u.job_title || u.email }))}
              testId="lead-input-owner"
            />
          </Field>
          <Field label="Notes">
            <textarea className="pg-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="lead-input-notes" />
          </Field>
        </form>
        <div className="px-6 py-4 flex justify-end gap-2" style={{ borderTop: "1px solid var(--pg-border)" }}>
          <button type="button" className="pg-btn" onClick={onClose}>Cancel</button>
          <button type="submit" onClick={submit} className="pg-btn pg-btn-primary" disabled={saving} data-testid="lead-submit">
            {saving ? "Saving…" : "Create Lead"}
          </button>
        </div>
      </div>
      {dedupeMatches && (
        <DuplicateWarningModal
          matches={dedupeMatches}
          entityLabel="Lead"
          onCancel={() => setDedupeMatches(null)}
          onContinue={() => { setDedupeMatches(null); doCreate(); }}
          onOpenExisting={(row, kind) => {
            setDedupeMatches(null);
            onClose();
            navigate(kind === "leads" ? `/leads/${row.id}` : `/contacts/${row.id}`);
          }}
          onMergeInto={async (row, kind) => {
            const fields = ["email", "phone", "company", "source", "notes"];
            const patch = {};
            for (const f of fields) {
              if ((form[f] || "").toString().trim() && !(row[f] || "").toString().trim()) patch[f] = form[f];
            }
            if (form.company_id && !row.company_id) patch.company_id = form.company_id;
            try {
              if (Object.keys(patch).length) {
                await api.patch(kind === "leads" ? `/leads/${row.id}` : `/contacts/${row.id}`, patch);
                toast.success(`Merged your inputs into ${row.name}`);
              } else {
                toast.info(`No new info to merge into ${row.name}`);
              }
              setDedupeMatches(null);
              onClose();
              navigate(kind === "leads" ? `/leads/${row.id}` : `/contacts/${row.id}`);
            } catch {
              toast.error("Merge failed — please try again");
            }
          }}
        />
      )}
    </>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="block mono text-[11px] text-mute mb-1 uppercase tracking-wider">
        {label}{required && <span style={{ color: "var(--pg-accent)" }}> *</span>}
      </label>
      {children}
    </div>
  );
}
